import { LayoutType } from "../LayoutType";
import { TransitionConfig } from "../TransitionConfig";
import React, { useEffect, useRef } from 'react';
import { Sequence, AbsoluteFill, useCurrentFrame, useVideoConfig, Easing } from 'remotion';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { LayerItem } from './base';
import { LayoutBlock, resolveLayout } from '../LayoutResolver';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';
import { MediaObject } from '../media/types';
import { MediaRole, normalizeMediaRole } from '../MediaRole';
import { ObjectFit } from "./style";
import { AnimationHelpers } from '../helpers';
import { AnimatedItem, AnimationConfig, SoundConfig } from './animated';
import { OverlayTransitionConfig } from './overlay-transition';
import { SequenceTimingHelper, AnimationState } from '../helpers/SequenceTimingHelper';
import {
    MediaState,
    SerializableState,
    SceneState,
    SerializedEffectState,
    Serializable
} from './serializable';
import TimelineRegistry from '../TimelineRegistry';
import { validateSceneState } from '../schemas/timeline-schema';
import { deserializeEffects, serializeEffects } from '../effects/serialization';


// Debug logging control - disabled in production builds
const DEBUG_SCENES = process.env.NODE_ENV !== 'production';

function hasMediaExportState(input: MediaObject): input is MediaObject & { exportState(): MediaState } {
    const candidate = input as { exportState?: unknown };
    return typeof candidate.exportState === 'function';
}

function mediaRoleOrMain(role: string | undefined): MediaRole {
    return normalizeMediaRole(role);
}

function exportMediaEffects(input: MediaObject): { effects?: SerializedEffectState[] } {
    const effects = serializeEffects(input.effects);
    return effects.length > 0 ? { effects } : {};
}

function exportMediaState(input: MediaObject): MediaState {
    if (hasMediaExportState(input)) {
        return input.exportState();
    }

    console.warn(`[Scene] Media object ${input.id} doesn't have exportState method`);

    switch (input.type) {
        case 'video':
            return {
                type: 'VideoMedia',
                version: '1.0.0',
                id: input.id,
                role: input.role,
                src: input.src,
                offset: input.offset,
                duration: input.duration,
                volume: input.volume,
                muted: input.muted,
                transparencyMode: input.transparencyMode,
                crop: input.crop,
                usePartialDownload: input.usePartialDownload,
                ...exportMediaEffects(input)
            };
        case 'image':
            return {
                type: 'ImageMedia',
                version: '1.0.0',
                id: input.id,
                role: input.role,
                src: input.src,
                duration: input.duration,
                crop: input.crop,
                ...exportMediaEffects(input)
            };
        case 'text':
            return {
                type: 'TextMedia',
                version: '1.0.0',
                id: input.id,
                role: input.role,
                text: input.text,
                content: input.content
            };
        case 'generated':
            return {
                type: 'GeneratedSlide',
                version: '1.0.0',
                id: input.id,
                role: input.role,
                content: input.content,
                ...exportMediaEffects(input)
            };
    }
}

function getMediaObjectSource(media: MediaObject | null): string | undefined {
    return media?.type === 'video' || media?.type === 'image'
        ? media.src
        : undefined;
}

type AnimationStylesWithMetadata = React.CSSProperties & {
    animationClass?: string;
    animationName?: string;
    animationPrefix?: string;
};

function extractAnimationMetadata(styles: React.CSSProperties): {
    cleanStyles: React.CSSProperties;
    cssAnimationClass?: string;
    cssAnimationPrefix: string;
} {
    const stylesWithMetadata = styles as AnimationStylesWithMetadata;
    const { animationClass, animationPrefix } = stylesWithMetadata;
    const cleanStyles: AnimationStylesWithMetadata = { ...stylesWithMetadata };
    delete cleanStyles.animationClass;
    delete cleanStyles.animationName;
    delete cleanStyles.animationPrefix;

    return {
        cleanStyles,
        cssAnimationClass: animationClass,
        cssAnimationPrefix: animationPrefix || 'animate__'
    };
}

interface SceneSequenceProps {
    from: number;
    durationInFrames: number;
    name?: string;
    premountFor?: number;
}

export interface SceneItem extends LayerItem, AnimatedItem {
    layout: LayoutType;
    inputs: MediaObject[];
    objectFit?: ObjectFit;
    overrideTransitions?: Partial<{
      in: TransitionConfig;
      out: TransitionConfig;
    }>;
    // Overlay transition that plays at the end of this scene, bridging to the next scene
    endOverlayTransition?: OverlayTransitionConfig;
    // NEW: Optional per-input animation configuration
    inputAnimations?: {
        [inputIndex: number]: {
            entranceAnimation?: AnimationConfig;
            exitAnimation?: AnimationConfig;
            entranceSoundEffect?: SoundConfig;
            exitSoundEffect?: SoundConfig;
        };
    };
    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode;
}

export class SceneItemImpl implements SceneItem, Serializable {
    id: string;
    startAt: number;
    duration?: number;  // Make duration optional
    layout: LayoutType;
    inputs: MediaObject[];
    objectFit?: ObjectFit;
    overrideTransitions?: Partial<{
      in: TransitionConfig;
      out: TransitionConfig;
    }>;
    // Overlay transition configuration
    endOverlayTransition?: OverlayTransitionConfig;
    // AnimatedItem properties
    entranceAnimation?: AnimationConfig;
    exitAnimation?: AnimationConfig;
    entranceSoundEffect?: SoundConfig;
    exitSoundEffect?: SoundConfig;
    customAnimations: AnimationConfig[];
    customSoundEffects: SoundConfig[];
    // NEW: Per-input animation configuration
    inputAnimations?: {
        [inputIndex: number]: {
            entranceAnimation?: AnimationConfig;
            exitAnimation?: AnimationConfig;
            entranceSoundEffect?: SoundConfig;
            exitSoundEffect?: SoundConfig;
        };
    };

    constructor(scene: Omit<SceneItem, 'render'>) {
        this.id = scene.id;
        this.startAt = scene.startAt;
        this.duration = scene.duration;
        this.layout = scene.layout;
        this.inputs = scene.inputs;
        this.objectFit = scene.objectFit;
        this.overrideTransitions = scene.overrideTransitions;
        this.endOverlayTransition = scene.endOverlayTransition;
        // Initialize AnimatedItem properties
        this.entranceAnimation = scene.entranceAnimation;
        this.exitAnimation = scene.exitAnimation;
        this.entranceSoundEffect = scene.entranceSoundEffect;
        this.exitSoundEffect = scene.exitSoundEffect;
        this.customAnimations = scene.customAnimations ?? [];
        this.customSoundEffects = scene.customSoundEffects ?? [];
        // Initialize per-input animations
        this.inputAnimations = scene.inputAnimations;
    }

    /**
     * Export Scene to serializable state
     */
    exportState(): SceneState {
        const state: SceneState = {
            type: 'Scene',
            version: '1.0.0',
            id: this.id,
            startAt: this.startAt,
            duration: this.duration !== undefined ? this.duration : 0,
            layout: this.layout,
            inputs: this.inputs.map(exportMediaState)
        };

        // Add optional properties
        if (this.objectFit) {
            state.objectFit = this.objectFit;
        }

        // Convert TransitionConfig to TransitionState if present
        if (this.overrideTransitions?.in) {
            const transitionIn = this.overrideTransitions.in;
            state.transitionIn = {
                type: transitionIn.type,
                duration: transitionIn.duration,
                mode: transitionIn.mode,
                ...('direction' in transitionIn && transitionIn.direction !== undefined ? { direction: transitionIn.direction } : {}),
                ...('easing' in transitionIn && transitionIn.easing !== undefined ? { easing: transitionIn.easing } : {})
            };
        }

        if (this.overrideTransitions?.out) {
            const transitionOut = this.overrideTransitions.out;
            state.transitionOut = {
                type: transitionOut.type,
                duration: transitionOut.duration,
                mode: transitionOut.mode,
                ...('direction' in transitionOut && transitionOut.direction !== undefined ? { direction: transitionOut.direction } : {}),
                ...('easing' in transitionOut && transitionOut.easing !== undefined ? { easing: transitionOut.easing } : {})
            };
        }

        return state;
    }

    /**
     * Import Scene from serialized state
     */
    static importState(state: SerializableState): SceneItemImpl {
        const validatedState = validateSceneState(state);

        // Import media objects using the registry
        const inputs = validatedState.inputs.map(mediaState => {
            try {
                return TimelineRegistry.import<MediaObject>(mediaState);
            } catch (error) {
                console.warn(`[Scene] Failed to import media, using fallback:`, error);
                // Fallback to direct instantiation if registry not set up
                if (mediaState.type === 'VideoMedia') {
                    return {
                        id: mediaState.id,
                        type: 'video' as const,
                        role: mediaRoleOrMain(mediaState.role),
                        src: mediaState.src,
                        offset: mediaState.offset,
                        duration: mediaState.duration,
                        volume: mediaState.volume,
                        muted: mediaState.muted,
                        transparencyMode: mediaState.transparencyMode,
                        effects: deserializeEffects(mediaState.effects),
                        crop: mediaState.crop,
                        usePartialDownload: mediaState.usePartialDownload,
                        render: () => null // Placeholder
                    };
                }

                if (mediaState.type === 'ImageMedia') {
                    return {
                        id: mediaState.id,
                        type: 'image' as const,
                        role: mediaRoleOrMain(mediaState.role),
                        src: mediaState.src,
                        duration: mediaState.duration,
                        effects: deserializeEffects(mediaState.effects),
                        crop: mediaState.crop,
                        render: () => null // Placeholder
                    };
                }

                if (mediaState.type === 'TextMedia') {
                    return {
                        id: mediaState.id,
                        type: 'text' as const,
                        role: mediaRoleOrMain(mediaState.role),
                        text: mediaState.text,
                        content: mediaState.content,
                        render: () => null
                    };
                }

                return {
                    id: mediaState.id,
                    type: 'generated' as const,
                    role: mediaRoleOrMain(mediaState.role),
                    content: mediaState.content,
                    effects: deserializeEffects(mediaState.effects),
                    render: () => null
                };
            }
        });

        // Convert TransitionState back to TransitionConfig
        const overrideTransitions: Partial<{
            in: TransitionConfig;
            out: TransitionConfig;
        }> = {};

        if (validatedState.transitionIn) {
            overrideTransitions.in = {
                type: validatedState.transitionIn.type,
                duration: validatedState.transitionIn.duration,
                mode: validatedState.transitionIn.mode || 'prefix',
                ...(validatedState.transitionIn.direction !== undefined ? {direction: validatedState.transitionIn.direction} : {}),
                ...(validatedState.transitionIn.easing !== undefined ? {easing: validatedState.transitionIn.easing} : {})
            };
        }

        if (validatedState.transitionOut) {
            overrideTransitions.out = {
                type: validatedState.transitionOut.type,
                duration: validatedState.transitionOut.duration,
                mode: validatedState.transitionOut.mode || 'postfix',
                ...(validatedState.transitionOut.direction !== undefined ? {direction: validatedState.transitionOut.direction} : {}),
                ...(validatedState.transitionOut.easing !== undefined ? {easing: validatedState.transitionOut.easing} : {})
            };
        }

        return new SceneItemImpl({
            id: validatedState.id,
            startAt: validatedState.startAt,
            duration: validatedState.duration,
            layout: validatedState.layout as LayoutType,
            inputs,
            objectFit: validatedState.objectFit as ObjectFit,
            ...(Object.keys(overrideTransitions).length > 0 && { overrideTransitions })
        });
    }


    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode {
        if (process.env.NODE_ENV === 'development') {
            if (DEBUG_SCENES) console.log(`[SCENE RENDER START] ${this.id} render method called`);
        }
        return (
            <SceneWithVideoConfig
                scene={this}
                passedFps={fps}
                theme={theme}
                aspectRatio={aspectRatio}
                defaults={defaults}
                videoConfig={videoConfig}
            />
        );
    }
}

const SceneWithVideoConfig: React.FC<{
    scene: SceneItemImpl;
    passedFps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig?: { width: number; height: number };
}> = ({ scene, passedFps, theme, aspectRatio, defaults, videoConfig }) => {
    const remotionVideoConfig = useVideoConfig();
    
    // CRITICAL FIX: Use Remotion's fps instead of passed fps to ensure consistency
    const consistentFps = remotionVideoConfig.fps;
    
    if (process.env.NODE_ENV === 'development') {
        if (DEBUG_SCENES) console.log(`[FPS CONSISTENCY] Scene ${scene.id}: passed fps=${passedFps}, remotion fps=${consistentFps}, using remotion fps`);
    }
    
    if (!videoConfig) {
        throw new Error('SceneItemImpl.render requires videoConfig parameter');
    }
    const { width, height } = videoConfig;

    // CRITICAL DEBUG: Check scene inputs before resolveLayout
    if (process.env.NODE_ENV === 'development') {
        if (DEBUG_SCENES) console.log(`[SCENE INPUTS DEBUG] ${scene.id} has ${scene.inputs.length} inputs:`, scene.inputs.map((input, i) => ({
            index: i,
            type: input.type,
            id: input.id,
            src: input.type === 'video' ? input.src : undefined
        })));
    }

    const layoutBlocks = resolveLayout(scene.layout, scene.inputs, defaults?.layoutPadding ?? 0.05, width, height);

    if (process.env.NODE_ENV === 'development') {
        if (DEBUG_SCENES) console.log(`[LAYOUT BLOCKS DEBUG] ${scene.id} resolveLayout returned ${layoutBlocks.length} blocks:`, layoutBlocks.map((block, i) => ({
            index: i,
            hasMedia: !!block.media,
            mediaType: block.media?.type,
            mediaSrc: block.media?.type === 'video' ? block.media.src : undefined
        })));
    }

    // Use helper to calculate sequence timing including transitions
    // Fall back to defaults if no override transitions are specified
    const effectiveTransitionIn: TransitionConfig = scene.overrideTransitions?.in || defaults?.transitionIn || { type: 'fade', duration: 0.5, mode: 'prefix' };
    const effectiveTransitionOut: TransitionConfig = scene.overrideTransitions?.out || defaults?.transitionOut || { type: 'fade', duration: 0.5, mode: 'postfix' };
    
    if (process.env.NODE_ENV === 'development') {
        if (DEBUG_SCENES) console.log(`[SCENE TRANSITION DEBUG] ${scene.id} transition selection:`, {
            hasOverrideIn: !!scene.overrideTransitions?.in,
            hasOverrideOut: !!scene.overrideTransitions?.out,
            effectiveInType: effectiveTransitionIn?.type,
            effectiveOutType: effectiveTransitionOut?.type,
            defaultInType: defaults?.transitionIn?.type,
            defaultOutType: defaults?.transitionOut?.type
        });
    }
    
    const { startFrame, durationFrames, entranceTransitionSkipped, entranceTransitionClipped } = AnimationHelpers.calculateSceneTransitionTiming(
        scene.startAt,
        scene.duration,
        consistentFps,
        effectiveTransitionIn,
        effectiveTransitionOut
    );

    const sequenceProps: SceneSequenceProps = {
        from: startFrame,
        durationInFrames: durationFrames
    };

    if (process.env.NODE_ENV === 'development') {
        if (DEBUG_SCENES) console.log(`[SEQUENCE PROPS DEBUG] ${scene.id}:`, { key: scene.id, ...sequenceProps });
        if (DEBUG_SCENES) console.log(`[CRITICAL FRAME DEBUG] ${scene.id} sequence will run from frame ${startFrame} to frame ${startFrame + durationFrames - 1}`);
        if (DEBUG_SCENES) console.log(`[CRITICAL FRAME DEBUG] ${scene.id} at ${consistentFps}fps: starts at ${startFrame/consistentFps}s, ends at ${(startFrame + durationFrames)/consistentFps}s`);
    }
    
    // CRITICAL DEBUG: Scene 2 sequence props verification
    if (process.env.NODE_ENV === 'development' && (scene.id === 'transition-scene-2' || scene.id === 'scene2')) {
        if (DEBUG_SCENES) console.log(`[SCENE 2 SEQUENCE PROPS] Remotion sequence configuration:`, {
            from: sequenceProps.from,
            durationInFrames: sequenceProps.durationInFrames,
            fromSeconds: sequenceProps.from / consistentFps,
            durationSeconds: sequenceProps.durationInFrames / consistentFps,
            sceneScheduledStart: scene.startAt,
            transitionDuration: effectiveTransitionIn?.duration,
            calculatedPreRoll: scene.startAt - (effectiveTransitionIn?.duration || 0)
        });
    }
    
    return (
        <DynamicSceneSequence
            scene={scene}
            sequenceProps={sequenceProps}
            effectiveTransitionIn={effectiveTransitionIn}
            effectiveTransitionOut={effectiveTransitionOut}
            entranceTransitionSkipped={entranceTransitionSkipped}
            entranceTransitionClipped={entranceTransitionClipped}
            layoutBlocks={layoutBlocks}
            fps={consistentFps}
            theme={theme}
            aspectRatio={aspectRatio}
            defaults={defaults}
            videoConfig={videoConfig}
        />
    );
};

/**
 * DynamicSceneSequence wrapper that changes its name based on current transition state
 */
const DynamicSceneSequence: React.FC<{
    scene: SceneItemImpl;
    sequenceProps: SceneSequenceProps;
    effectiveTransitionIn?: TransitionConfig;
    effectiveTransitionOut?: TransitionConfig;
    entranceTransitionSkipped?: boolean;
    entranceTransitionClipped?: boolean;
    layoutBlocks: LayoutBlock[];
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig: { width: number; height: number };
}> = ({ scene, sequenceProps, effectiveTransitionIn, effectiveTransitionOut, entranceTransitionSkipped, entranceTransitionClipped, layoutBlocks, fps, theme, aspectRatio, defaults, videoConfig }) => {
    const currentFrame = useCurrentFrame();
    // CRITICAL FIX: currentFrame is already relative to the sequence, don't add sequenceProps.from
    // The sequence starts at sequenceProps.from in the global timeline
    // currentFrame is the frame within this sequence (starting from 0)
    const globalFrame = currentFrame + sequenceProps.from;
    const currentTime = (currentFrame + sequenceProps.from) / fps;
    
    if (process.env.NODE_ENV === 'development') {
        if (DEBUG_SCENES) console.log(`[DYNAMIC SEQUENCE] ${scene.id} frame details:`, {
            localFrame: currentFrame,
            sequenceFrom: sequenceProps.from,
            globalFrame: globalFrame,
            currentTime: currentTime.toFixed(3),
            sceneStartAt: scene.startAt,
            expectedSequenceStart: scene.startAt - (effectiveTransitionIn ? effectiveTransitionIn.duration : 0)
        });
    }
    
    // CRITICAL DEBUG: For transition test scenes specifically
    if (process.env.NODE_ENV === 'development' && (scene.id === 'transition-scene-1' || scene.id === 'transition-scene-2' || scene.id === 'scene2')) {
        if (DEBUG_SCENES) console.log(`[${scene.id.toUpperCase()} DEBUG] Critical timing analysis:`, {
            sceneStartAt: scene.startAt,
            transitionDuration: effectiveTransitionIn?.duration,
            expectedSequenceStart: scene.startAt - (effectiveTransitionIn?.duration || 0),
            actualSequenceFrom: sequenceProps.from,
            actualSequenceFromSeconds: sequenceProps.from / fps,
            currentFrameInSequence: currentFrame,
            expectedEntranceFrames: effectiveTransitionIn ? Math.round(effectiveTransitionIn.duration * fps) : 0,
            isInEntrancePeriod: currentFrame < (effectiveTransitionIn ? Math.round(effectiveTransitionIn.duration * fps) : 0),
            entranceTransitionSkipped,
            entranceTransitionClipped
        });
    }
    
    // Determine current animation state with frame-accurate timing
    let animationState = 'VISIBLE';
    let activeTransition = '';
    
    const contentEndTime = scene.startAt + (scene.duration || 0);
    const contentEndFrame = Math.round(contentEndTime * fps);
    
    // Debug logging with frame-accurate information
    if (DEBUG_SCENES) console.log(`[DYNAMIC NAME DEBUG] ${scene.id} at frame ${globalFrame} (${currentTime.toFixed(3)}s):`, {
        sceneStart: scene.startAt,
        sceneDuration: scene.duration,
        contentEndTime,
        contentEndFrame,
        currentFrame: globalFrame,
        currentTime,
        hasTransitionIn: !!effectiveTransitionIn,
        hasTransitionOut: !!effectiveTransitionOut,
        transitionInType: effectiveTransitionIn?.type,
        transitionOutType: effectiveTransitionOut?.type
    });
    
    // Check if we're in entrance transition period (frame-accurate)
    // CRITICAL FIX: Use local currentFrame for state detection, not globalFrame
    // Only show ENTRANCE state if transition is not skipped
    if (effectiveTransitionIn && effectiveTransitionIn.mode === 'prefix' && !entranceTransitionSkipped) {
        const transitionDuration = effectiveTransitionIn.duration;
        const transitionDurationFrames = Math.round(transitionDuration * fps);
        
        // Handle clipped transitions differently
        if (entranceTransitionClipped) {
            // For clipped transitions, the visible portion starts at frame 0
            const visibleTransitionFrames = Math.round(scene.startAt * fps); // Only the visible portion
            if (process.env.NODE_ENV === 'development') {
                if (DEBUG_SCENES) console.log(`[ENTRANCE CHECK CLIPPED] ${scene.id}: local frame ${currentFrame}, visible transition ${visibleTransitionFrames} frames`);
            }
            
            if (currentFrame >= 0 && currentFrame < visibleTransitionFrames) {
                animationState = 'ENTRANCE';
                activeTransition = effectiveTransitionIn.type;
                // Progress calculation accounts for the clipped portion
                const totalTransitionFrames = transitionDurationFrames;
                const clippedFrames = totalTransitionFrames - visibleTransitionFrames;
                const adjustedProgress = (clippedFrames + currentFrame) / totalTransitionFrames;
                if (process.env.NODE_ENV === 'development') {
                    if (DEBUG_SCENES) console.log(`[ENTRANCE ACTIVE CLIPPED] ${scene.id}: ${activeTransition} transition, adjusted progress: ${(adjustedProgress * 100).toFixed(1)}%`);
                }
            }
        } else {
            // Normal entrance transition
            if (process.env.NODE_ENV === 'development') {
                if (DEBUG_SCENES) console.log(`[ENTRANCE CHECK] ${scene.id}: local frame ${currentFrame}, transition duration ${transitionDurationFrames} frames`);
            }
            
            // CRITICAL DEBUG: Show detailed entrance transition analysis
            if (process.env.NODE_ENV === 'development' && (scene.id === 'transition-scene-2' || scene.id === 'scene2')) {
                if (DEBUG_SCENES) console.log(`[SCENE 2 ENTRANCE ANALYSIS] Detailed entrance check:`, {
                    currentFrame,
                    transitionDurationFrames,
                    isInEntrancePeriod: currentFrame < transitionDurationFrames,
                    transitionProgress: currentFrame / transitionDurationFrames,
                    shouldShowEntrance: currentFrame < transitionDurationFrames,
                    frameRange: `0-${transitionDurationFrames-1} is entrance, ${transitionDurationFrames}+ is content`
                });
            }
            
            if (currentFrame < transitionDurationFrames) {
                animationState = 'ENTRANCE';
                activeTransition = effectiveTransitionIn.type;
                const transitionProgress = currentFrame / transitionDurationFrames;
                if (process.env.NODE_ENV === 'development') {
                    if (DEBUG_SCENES) console.log(`[ENTRANCE ACTIVE] ${scene.id}: ${activeTransition} transition active at local frame ${currentFrame}, progress: ${(transitionProgress * 100).toFixed(1)}%`);
                }
            } else {
                if (process.env.NODE_ENV === 'development') {
                    if (DEBUG_SCENES) console.log(`[ENTRANCE COMPLETE] ${scene.id}: local frame ${currentFrame} >= transition end ${transitionDurationFrames}`);
                }
            }
        }
    } else if (entranceTransitionSkipped) {
        if (process.env.NODE_ENV === 'development') {
            if (DEBUG_SCENES) console.log(`[ENTRANCE SKIPPED] ${scene.id}: Scene starts at 0, no entrance transition`);
        }
        // Add debug info about why it was skipped
        if (process.env.NODE_ENV === 'development') {
            if (DEBUG_SCENES) console.log(`[ENTRANCE SKIPPED DEBUG] ${scene.id}: sceneStartAt=${scene.startAt}, hasTransitionIn=${!!effectiveTransitionIn}, transitionDuration=${effectiveTransitionIn?.duration}`);
        }
    } else if (effectiveTransitionIn && effectiveTransitionIn.mode === 'prefix') {
        // Transition exists but was skipped for timing reasons
        if (process.env.NODE_ENV === 'development') {
            if (DEBUG_SCENES) console.log(`[ENTRANCE NOT APPLICABLE] ${scene.id}: Entrance transition exists but conditions not met for ENTRANCE state`);
        }
    }
    
    // Check if we're in exit transition period (frame-accurate)
    // CRITICAL FIX: Use local currentFrame for state detection
    if (effectiveTransitionOut && effectiveTransitionOut.mode === 'postfix' && scene.duration) {
        const transitionDuration = effectiveTransitionOut.duration;
        const transitionDurationFrames = Math.round(transitionDuration * fps);
        const contentDurationFrames = Math.round(scene.duration * fps);
        
        // Calculate frames including entrance transition if present
        let contentStartFrame = 0;
        if (effectiveTransitionIn && effectiveTransitionIn.mode === 'prefix' && !entranceTransitionSkipped) {
            if (entranceTransitionClipped) {
                // For clipped transitions, content starts after the visible portion
                contentStartFrame = Math.round(scene.startAt * fps);
            } else {
                contentStartFrame = Math.round(effectiveTransitionIn.duration * fps);
            }
        }
        
        const contentEndFrame = contentStartFrame + contentDurationFrames;
        
        if (process.env.NODE_ENV === 'development') {
            if (DEBUG_SCENES) console.log(`[EXIT CHECK] ${scene.id}: local frame ${currentFrame}, content ends at ${contentEndFrame}, exit duration ${transitionDurationFrames} frames`);
        }
        
        if (currentFrame >= contentEndFrame && currentFrame < contentEndFrame + transitionDurationFrames) {
            animationState = 'EXIT';
            activeTransition = effectiveTransitionOut.type;
            const transitionProgress = (currentFrame - contentEndFrame) / transitionDurationFrames;
            if (process.env.NODE_ENV === 'development') {
                if (DEBUG_SCENES) console.log(`[EXIT ACTIVE] ${scene.id}: ${activeTransition} transition active at local frame ${currentFrame}, progress: ${(transitionProgress * 100).toFixed(1)}%`);
            }
        } else if (currentFrame < contentEndFrame) {
            if (process.env.NODE_ENV === 'development') {
                if (DEBUG_SCENES) console.log(`[EXIT NOT YET] ${scene.id}: local frame ${currentFrame} before exit transition start ${contentEndFrame}`);
            }
        } else if (currentFrame >= contentEndFrame + transitionDurationFrames) {
            if (process.env.NODE_ENV === 'development') {
                if (DEBUG_SCENES) console.log(`[EXIT COMPLETE] ${scene.id}: local frame ${currentFrame} after exit transition end ${contentEndFrame + transitionDurationFrames}`);
            }
        }
    }
    
    // Create detailed dynamic name with frame information
    const baseName = `Scene-${scene.id}`;
    
    // Calculate actual end time including transitions
    let actualEndTime = contentEndTime;
    if (effectiveTransitionOut && effectiveTransitionOut.mode === 'postfix') {
        actualEndTime += effectiveTransitionOut.duration;
    }
    
    const timeInfo = `${scene.startAt}s→${actualEndTime}s`;
    const frameInfo = `F${sequenceProps.from}→${sequenceProps.from + sequenceProps.durationInFrames - 1}`;
    
    let stateLabel = '';
    if (animationState !== 'VISIBLE') {
        const currentRelativeFrame = globalFrame - sequenceProps.from;
        stateLabel = `-${animationState}-${activeTransition}-F${currentRelativeFrame}`;
    } else {
        const currentRelativeFrame = globalFrame - sequenceProps.from;
        stateLabel = `-VISIBLE-F${currentRelativeFrame}`;
    }
    
    const dynamicName = `${baseName}-${timeInfo}-${frameInfo}${stateLabel}`;

    if (process.env.NODE_ENV === 'development') {
        if (DEBUG_SCENES) console.log(`[SEQUENCE NAME UPDATE] ${scene.id}: "${dynamicName}"`);
    }

    // Calculate premount frames for video seeking
    // Browser can handle 1 frame premount, but cloud rendering needs MUCH more time
    // to seek to startFrom position. Use 200 frames (8 seconds at 25fps) for cloud.
    const hasVideoWithOffset = scene.inputs?.some(input =>
        input.type === 'video' && input.offset && input.offset > 0
    );

    // Always use 200 frames premount for videos with offsets
    // Cloud rendering is much slower at seeking than browser preview
    const premountFrames = hasVideoWithOffset ? 200 : (defaults?.premountFrames ?? 0);

    // Debug logging for cloud rendering (uses module-level DEBUG_SCENES constant)
    if (hasVideoWithOffset && process.env.NODE_ENV === 'development' && DEBUG_SCENES) {
        console.log(`[SCENE PREMOUNT] ${scene.id}: premountFrames=${premountFrames}, hasVideoWithOffset=${hasVideoWithOffset}`);
    }

    // Update sequence props with dynamic name and premounting
    const dynamicSequenceProps = {
        ...sequenceProps,
        name: dynamicName,
        ...(premountFrames > 0 && { premountFor: premountFrames })
    };

    return (
        <Sequence key={scene.id} {...dynamicSequenceProps}>
            <AbsoluteFill>
                <SceneItemContent
                    scene={scene}
                    layoutBlocks={layoutBlocks}
                    fps={fps}
                    theme={theme}
                    aspectRatio={aspectRatio}
                    defaults={defaults}
                    videoConfig={videoConfig}
                    effectiveTransitionIn={effectiveTransitionIn}
                    effectiveTransitionOut={effectiveTransitionOut}
                    entranceTransitionSkipped={entranceTransitionSkipped}
                    entranceTransitionClipped={entranceTransitionClipped}
                />
            </AbsoluteFill>
        </Sequence>
    );
};

/**
 * SceneItemContent component handles animation integration within Remotion context
 */
const SceneItemContent: React.FC<{
    scene: SceneItemImpl;
    layoutBlocks: LayoutBlock[];
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig: { width: number; height: number };
    effectiveTransitionIn?: TransitionConfig;
    effectiveTransitionOut?: TransitionConfig;
    entranceTransitionSkipped?: boolean;
    entranceTransitionClipped?: boolean;
}> = ({ scene, layoutBlocks, fps, defaults, videoConfig, effectiveTransitionIn, effectiveTransitionOut, entranceTransitionSkipped }) => {
    const currentFrame = useCurrentFrame();
    const remotionVideoConfig = useVideoConfig();
    const lastFrameRef = useRef<number>(-1);
    
    // CRITICAL FIX: Use Remotion's fps consistently throughout
    const consistentFps = remotionVideoConfig.fps;
    
    // Track when component stops executing
    useEffect(() => {
        const expectedEndFrame = Math.round((scene.startAt + (scene.duration || 0) + 1) * consistentFps); // +1s for exit transition
        
        if (lastFrameRef.current !== -1 && currentFrame > lastFrameRef.current + 1) {
            if (process.env.NODE_ENV === 'development') {
                if (DEBUG_SCENES) console.log(`[SCENE EXECUTION GAP] ${scene.id} skipped from frame ${lastFrameRef.current} to ${currentFrame}`);
            }
        }
        
        if (currentFrame >= expectedEndFrame - 5) { // Log when approaching end
            if (process.env.NODE_ENV === 'development') {
                if (DEBUG_SCENES) console.log(`[SCENE NEAR END] ${scene.id} at frame ${currentFrame}, expected end frame ${expectedEndFrame}`);
            }
        }
        
        lastFrameRef.current = currentFrame;
        
        return () => {
            if (process.env.NODE_ENV === 'development') {
                if (DEBUG_SCENES) console.log(`[SCENE EXECUTION END] ${scene.id} component cleanup at frame ${currentFrame}`);
            }
        };
    }, [currentFrame, scene.id, scene.startAt, scene.duration, consistentFps]);
    
    if (process.env.NODE_ENV === 'development') {
        if (DEBUG_SCENES) console.log(`[SCENE CONTENT START] ${scene.id} component executing at frame ${currentFrame}`);
        if (DEBUG_SCENES) console.log(`[FPS CONSISTENCY] SceneItemContent: passed fps=${fps}, remotion fps=${consistentFps}, using remotion fps`);
        if (DEBUG_SCENES) console.log(`[FRAME TRACKING] ${scene.id} executing at currentFrame=${currentFrame}, globalFrame=${currentFrame + Math.round(scene.startAt * consistentFps)}`);
    }
    
    // Calculate global frame from local sequence frame
    const sceneStartFrame = Math.round(scene.startAt * consistentFps);
    const globalFrame = currentFrame + sceneStartFrame; // Convert local frame to global timeline
    
    // Debug logging for scene state
    const currentTime = globalFrame / consistentFps;
    const sceneEndTime = scene.duration ? scene.startAt + scene.duration : Infinity;
    if (process.env.NODE_ENV === 'development') {
        if (DEBUG_SCENES) console.log(`[SCENE DEBUG] ${scene.id} at frame ${globalFrame} (${currentTime.toFixed(2)}s):`, {
            sceneStart: scene.startAt,
            sceneDuration: scene.duration,
            sceneEnd: sceneEndTime === Infinity ? 'infinite' : sceneEndTime.toFixed(2),
            localFrame: currentFrame,
            globalFrame,
            hasOverrideIn: !!scene.overrideTransitions?.in,
            hasOverrideOut: !!scene.overrideTransitions?.out,
            consistentFps
        });
    }
    
    // UPDATED: Support both the new unified animation system AND legacy transitions
    const hasSceneAnimations = scene.entranceAnimation || scene.exitAnimation;
    const hasInputAnimations = scene.inputAnimations && Object.keys(scene.inputAnimations).length > 0;
    const hasTransitions = scene.overrideTransitions?.in || scene.overrideTransitions?.out || 
                          defaults?.transitionIn || defaults?.transitionOut;
    
    // Use passed effective transitions (already calculated with proper fallbacks)
    // const effectiveTransitionIn and effectiveTransitionOut are now props
    
    let animationStyles: React.CSSProperties = {};
    let visibility: React.CSSProperties = {};
    let animationState: AnimationState = AnimationState.VISIBLE;
    let cssAnimationClass: string | undefined;
    let cssAnimationPrefix: string | undefined;
    let transitionOpacity: number | undefined;
    let transitionTransform: string | undefined;
    let transitionClipPath: string | undefined;
    
    // Priority logic: Scene-level animations take precedence over input-level animations
    if (hasSceneAnimations) {
        // Scene-level animations: Apply to entire container (existing behavior)
        if (DEBUG_SCENES) console.log(`[SCENE_ANIMATION_INTEGRATION] ${scene.id}: Using scene-level unified animation system`);
        
        const animationResult = SequenceTimingHelper.getSequenceAnimationStyles(
            currentFrame,
            consistentFps,
            scene.duration,
            scene.entranceAnimation,
            scene.exitAnimation,
            undefined, // baseTransforms
            1 // defaultOpacity for scenes
        );
        
        animationStyles = animationResult.animationStyles;
        visibility = animationResult.visibility;
        animationState = animationResult.animationState;
        
        // Extract CSS animation classes like overlay system does
        const metadata = extractAnimationMetadata(animationStyles);
        cssAnimationClass = metadata.cssAnimationClass;
        cssAnimationPrefix = metadata.cssAnimationPrefix;
        animationStyles = metadata.cleanStyles;
        
        if (DEBUG_SCENES) console.log(`[SCENE_ANIMATION_DEBUG] ${scene.id}: Animation state ${animationState}, has CSS class: ${!!cssAnimationClass}`);
    } else if (hasInputAnimations) {
        // Input-level animations: Will be handled per-block in rendering
        if (DEBUG_SCENES) console.log(`[INPUT_ANIMATION_INTEGRATION] ${scene.id}: Using per-input animation system`);
        
        // Use basic visibility for container - animations applied per block
        const result = AnimationHelpers.getAnimationStyles(
            globalFrame,
            consistentFps,
            scene.startAt,
            scene.duration
        );
        visibility = result.visibility;
    } else if (hasTransitions) {
        // LEGACY: Use existing transition system only when no animations are present
        if (DEBUG_SCENES) console.log(`[SCENE_TRANSITION_LEGACY] ${scene.id}: Using legacy transition system`);
        
        // Use existing transition calculation for basic visibility
        const result = AnimationHelpers.getAnimationStyles(
            globalFrame,
            consistentFps,
            scene.startAt,
            scene.duration,
            undefined, // no individual entrance animation
            undefined, // no individual exit animation
            undefined, // no base transforms
            effectiveTransitionIn,
            effectiveTransitionOut
        );
        visibility = result.visibility;
        
        // Apply legacy transition visual effects 
        // Calculate transition progress for fade effects
        // Apply entrance transition fade/crossfade  
        if (effectiveTransitionIn && (effectiveTransitionIn.type === 'fade' || effectiveTransitionIn.type === 'crossfade') && effectiveTransitionIn.mode === 'prefix' && !entranceTransitionSkipped) {
            const transitionFrames = Math.round(effectiveTransitionIn.duration * fps);
            
            // CRITICAL FIX: Use local currentFrame for transitions, not global frame
            // The sequence already includes the pre-roll, so frame 0 is the start of transition
            if (currentFrame < transitionFrames) {
                // During entrance fade/crossfade
                const progress = currentFrame / transitionFrames;
                const nextTransitionOpacity = effectiveTransitionIn.type === 'crossfade'
                    ? Easing.out(Easing.ease)(progress)
                    : progress;
                
                transitionOpacity = nextTransitionOpacity;
                
                if (process.env.NODE_ENV === 'development') {
                    if (DEBUG_SCENES) console.log(`[ENTRANCE ${effectiveTransitionIn.type.toUpperCase()}] ${scene.id}: local frame ${currentFrame}, progress ${(progress * 100).toFixed(1)}%, opacity ${nextTransitionOpacity.toFixed(2)}`);
                }
            }
            // After transition frames, opacity remains at full (1.0) - no explicit setting needed
        }
        
        // Apply exit transition fade/crossfade
        if (effectiveTransitionOut && (effectiveTransitionOut.type === 'fade' || effectiveTransitionOut.type === 'crossfade') && effectiveTransitionOut.mode === 'postfix') {
            const transitionFrames = Math.round(effectiveTransitionOut.duration * fps);
            const contentDurationFrames = scene.duration ? Math.round(scene.duration * fps) : 0;
            const entranceTransitionFrames = effectiveTransitionIn ? Math.round(effectiveTransitionIn.duration * fps) : 0;
            const localContentEndFrame = entranceTransitionFrames + contentDurationFrames;
            
            if (currentFrame >= localContentEndFrame && currentFrame < localContentEndFrame + transitionFrames) {
                // During exit fade/crossfade
                const progress = (currentFrame - localContentEndFrame) / transitionFrames;
                const nextTransitionOpacity = effectiveTransitionOut.type === 'crossfade'
                    ? 1 - Easing.in(Easing.ease)(progress)
                    : 1 - progress;
                
                transitionOpacity = nextTransitionOpacity;
                
                if (DEBUG_SCENES) console.log(`[EXIT ${effectiveTransitionOut.type.toUpperCase()}] ${scene.id}: local frame ${currentFrame}, progress ${(progress * 100).toFixed(1)}%, opacity ${nextTransitionOpacity.toFixed(2)}`);
            }
        }
        
        // Apply entrance transition wipe
        if (effectiveTransitionIn && effectiveTransitionIn.type === 'wipe' && effectiveTransitionIn.mode === 'prefix' && !entranceTransitionSkipped) {
            const transitionFrames = Math.round(effectiveTransitionIn.duration * fps);
            
            // CRITICAL FIX: Use local currentFrame for transitions, not global frame
            if (currentFrame < transitionFrames) {
                const progress = currentFrame / transitionFrames;
                const direction = effectiveTransitionIn.direction || 'up';
                
                switch (direction) {
                    case 'up':
                        transitionClipPath = `inset(${(1 - progress) * 100}% 0 0 0)`;
                        break;
                    case 'down':
                        transitionClipPath = `inset(0 0 ${(1 - progress) * 100}% 0)`;
                        break;
                    case 'left':
                        transitionClipPath = `inset(0 ${(1 - progress) * 100}% 0 0)`;
                        break;
                    case 'right':
                        transitionClipPath = `inset(0 0 0 ${(1 - progress) * 100}%)`;
                        break;
                }
                if (DEBUG_SCENES) console.log(`[ENTRANCE WIPE ${direction.toUpperCase()}] ${scene.id}: local frame ${currentFrame}, progress ${(progress * 100).toFixed(1)}%, clipPath ${transitionClipPath}`);
            }
            // After transition frames, no clipping needed - scene is fully visible
        }
        
        // Apply exit transition wipe
        if (effectiveTransitionOut && effectiveTransitionOut.type === 'wipe' && effectiveTransitionOut.mode === 'postfix') {
            const transitionFrames = Math.round(effectiveTransitionOut.duration * fps);
            const contentDurationFrames = scene.duration ? Math.round(scene.duration * fps) : 0;
            const entranceTransitionFrames = effectiveTransitionIn ? Math.round(effectiveTransitionIn.duration * fps) : 0;
            const localContentEndFrame = entranceTransitionFrames + contentDurationFrames;
            
            if (currentFrame >= localContentEndFrame && currentFrame < localContentEndFrame + transitionFrames) {
                const progress = (currentFrame - localContentEndFrame) / transitionFrames;
                const direction = effectiveTransitionOut.direction || 'up';
                
                switch (direction) {
                    case 'up':
                        transitionClipPath = `inset(0 0 ${progress * 100}% 0)`;
                        break;
                    case 'down':
                        transitionClipPath = `inset(${progress * 100}% 0 0 0)`;
                        break;
                    case 'left':
                        transitionClipPath = `inset(0 0 0 ${progress * 100}%)`;
                        break;
                    case 'right':
                        transitionClipPath = `inset(0 ${progress * 100}% 0 0)`;
                        break;
                }
                if (DEBUG_SCENES) console.log(`[EXIT WIPE ${direction.toUpperCase()}] ${scene.id}: local frame ${currentFrame}, progress ${(progress * 100).toFixed(1)}%, clipPath ${transitionClipPath}`);
            }
        }
        
        // Apply entrance transition slide
        if (effectiveTransitionIn && effectiveTransitionIn.type === 'slide' && effectiveTransitionIn.mode === 'prefix' && !entranceTransitionSkipped) {
            const transitionFrames = Math.round(effectiveTransitionIn.duration * fps);
            
            // CRITICAL FIX: Use local currentFrame for transitions, not global frame
            if (currentFrame < transitionFrames) {
                const progress = currentFrame / transitionFrames;
                const direction = effectiveTransitionIn.direction || 'up';
                const easeProgress = progress; // Could apply easing here
                
                switch (direction) {
                    case 'up':
                        transitionTransform = `translateY(${(1 - easeProgress) * 100}%)`;
                        break;
                    case 'down':
                        transitionTransform = `translateY(${(easeProgress - 1) * 100}%)`;
                        break;
                    case 'left':
                        transitionTransform = `translateX(${(1 - easeProgress) * 100}%)`;
                        break;
                    case 'right':
                        transitionTransform = `translateX(${(easeProgress - 1) * 100}%)`;
                        break;
                }
                if (DEBUG_SCENES) console.log(`[ENTRANCE SLIDE ${direction.toUpperCase()}] ${scene.id}: local frame ${currentFrame}, progress ${(progress * 100).toFixed(1)}%, transform ${transitionTransform}`);
            }
            // After transition frames, no transform needed - scene is in final position
        }
        
        // Apply exit transition slide
        if (effectiveTransitionOut && effectiveTransitionOut.type === 'slide' && effectiveTransitionOut.mode === 'postfix') {
            const transitionFrames = Math.round(effectiveTransitionOut.duration * fps);
            const contentDurationFrames = scene.duration ? Math.round(scene.duration * fps) : 0;
            const entranceTransitionFrames = effectiveTransitionIn ? Math.round(effectiveTransitionIn.duration * fps) : 0;
            const localContentEndFrame = entranceTransitionFrames + contentDurationFrames;
            
            if (currentFrame >= localContentEndFrame && currentFrame < localContentEndFrame + transitionFrames) {
                const progress = (currentFrame - localContentEndFrame) / transitionFrames;
                const direction = effectiveTransitionOut.direction || 'up';
                const easeProgress = progress; // Could apply easing here
                
                switch (direction) {
                    case 'up':
                        transitionTransform = `translateY(${-easeProgress * 100}%)`;
                        break;
                    case 'down':
                        transitionTransform = `translateY(${easeProgress * 100}%)`;
                        break;
                    case 'left':
                        transitionTransform = `translateX(${-easeProgress * 100}%)`;
                        break;
                    case 'right':
                        transitionTransform = `translateX(${easeProgress * 100}%)`;
                        break;
                }
                if (DEBUG_SCENES) console.log(`[EXIT SLIDE ${direction.toUpperCase()}] ${scene.id}: local frame ${currentFrame}, progress ${(progress * 100).toFixed(1)}%, transform ${transitionTransform}`);
            }
        }
    } else {
        // No animations or transitions - use basic visibility
        if (DEBUG_SCENES) console.log(`[SCENE_NO_ANIMATION] ${scene.id}: No animations or transitions`);
        const result = AnimationHelpers.getAnimationStyles(
            globalFrame,
            consistentFps,
            scene.startAt,
            scene.duration
        );
        visibility = result.visibility;
    }

    // Debug final applied styles
    const finalStyles = {
        position: 'absolute' as const,
        width: '100%',
        height: '100%',
        ...animationStyles,
        ...visibility,
        // Apply transition opacity if set
        ...(transitionOpacity !== undefined && { opacity: transitionOpacity }),
        // Apply transition transform if set
        ...(transitionTransform !== undefined && { transform: transitionTransform }),
        // Apply transition clip-path if set
        ...(transitionClipPath !== undefined && { clipPath: transitionClipPath })
    };

    // Debug scene rendering at frame boundaries (development only)
    if (process.env.NODE_ENV === 'development' && DEBUG_SCENES) {
        if (currentFrame <= 2 || (scene.duration && currentFrame >= Math.floor(scene.duration * consistentFps) - 2)) {
            console.log(`[SCENE BOUNDARY] ${scene.id} at frame ${currentFrame}:`, {
                globalFrame,
                finalOpacity: finalStyles.opacity
            });
        }
    }
    
    if (DEBUG_SCENES) console.log(`[SCENE STYLE DEBUG] ${scene.id} final styles:`, {
        opacity: finalStyles.opacity,
        transform: finalStyles.transform,
        clipPath: finalStyles.clipPath,
        hasAnimationStyles: Object.keys(animationStyles).length > 0,
        hasVisibilityStyles: Object.keys(visibility).length > 0,
        hasTransitionOpacity: transitionOpacity !== undefined,
        hasTransitionTransform: transitionTransform !== undefined,
        hasTransitionClipPath: transitionClipPath !== undefined,
        transitionOpacity,
        transitionTransform,
        transitionClipPath,
        animationStyles,
        visibility
    });
    
    // CRITICAL DEBUG: Log if content is being hidden when it shouldn't be
    if (finalStyles.opacity === 0) {
        if (DEBUG_SCENES) console.log(`[CRITICAL VISIBILITY ERROR] ${scene.id} HIDDEN at frame ${globalFrame}:`, {
            sceneStart: scene.startAt,
            sceneDuration: scene.duration,
            contentEndTime: scene.startAt + (scene.duration || 0),
            hasTransitions,
            hasSceneAnimations,
            hasInputAnimations,
            visibility,
            currentTime: globalFrame / consistentFps
        });
    } else {
        if (DEBUG_SCENES) console.log(`[SCENE VISIBLE] ${scene.id} visible at frame ${globalFrame} with opacity ${finalStyles.opacity || 1}`);
    }
    
    if (DEBUG_SCENES) console.log(`[SCENE RENDER DEBUG] ${scene.id} rendering ${layoutBlocks.length} layout blocks`);
    
    // Enhanced: Check for complete invisibility including animation states
    if (animationState === AnimationState.INACTIVE) {
        if (DEBUG_SCENES) console.log(`[SCENE_ANIMATION_INACTIVE] ${scene.id} in INACTIVE state, not rendering`);
        return null;
    }
    
    // CRITICAL: Don't render if scene is completely invisible and past all transitions
    if (finalStyles.opacity === 0 && !hasSceneAnimations && !hasInputAnimations) {
        // Check if we're past all transitions (only for legacy transition system)
        const entranceTransitionFrames = effectiveTransitionIn && effectiveTransitionIn.mode === 'prefix' 
            ? Math.round(effectiveTransitionIn.duration * consistentFps) 
            : 0;
        const contentDurationFrames = scene.duration ? Math.round(scene.duration * consistentFps) : 0;
        const exitTransitionFrames = effectiveTransitionOut && effectiveTransitionOut.mode === 'postfix' 
            ? Math.round(effectiveTransitionOut.duration * consistentFps) 
            : 0;
        
        const totalSequenceFrames = entranceTransitionFrames + contentDurationFrames + exitTransitionFrames;
        
        if (currentFrame >= totalSequenceFrames) {
            if (DEBUG_SCENES) console.log(`[SCENE HIDDEN] ${scene.id} completely hidden and past all transitions at frame ${currentFrame}, not rendering`);
            return null;
        }
    }
    
    // Build className for CSS animations (like overlay system)
    let className = '';
    if (cssAnimationClass) {
        className = `${cssAnimationPrefix}animated ${cssAnimationClass}`;
        if (DEBUG_SCENES) console.log(`[SCENE_CSS_ANIMATION] ${scene.id}: Applying CSS classes: ${className}`);
    }
    
    return (
        <div
            style={finalStyles}
            className={className}
        >
            {layoutBlocks.map((block, index) => {
                if (DEBUG_SCENES) console.log(`[BLOCK RENDER DEBUG] ${scene.id}-block-${index}:`, {
                    hasMedia: !!block.media,
                    mediaType: block.media?.type,
                    mediaSrc: getMediaObjectSource(block.media),
                    blockPosition: { x: block.x, y: block.y, width: block.width, height: block.height }
                });
                
                // Check if this input has individual animations
                const inputAnimation = hasInputAnimations && scene.inputAnimations ? scene.inputAnimations[index] : undefined;
                let blockAnimationStyles: React.CSSProperties = {};
                let blockVisibility: React.CSSProperties = {};
                let blockCssAnimationClass: string | undefined;
                let blockCssAnimationPrefix: string | undefined;
                
                if (inputAnimation && (inputAnimation.entranceAnimation || inputAnimation.exitAnimation)) {
                    if (DEBUG_SCENES) console.log(`[INPUT_ANIMATION_DEBUG] ${scene.id}-block-${index}: Applying individual animations`);
                    
                    const inputAnimationResult = SequenceTimingHelper.getSequenceAnimationStyles(
                        currentFrame,
                        consistentFps,
                        scene.duration,
                        inputAnimation.entranceAnimation,
                        inputAnimation.exitAnimation,
                        undefined, // baseTransforms
                        1 // defaultOpacity
                    );
                    
                    blockAnimationStyles = inputAnimationResult.animationStyles;
                    blockVisibility = inputAnimationResult.visibility;
                    
                    // Extract CSS animation classes
                    const metadata = extractAnimationMetadata(blockAnimationStyles);
                    blockCssAnimationClass = metadata.cssAnimationClass;
                    blockCssAnimationPrefix = metadata.cssAnimationPrefix;
                    blockAnimationStyles = metadata.cleanStyles;
                    
                    if (DEBUG_SCENES) console.log(`[INPUT_ANIMATION_DEBUG] ${scene.id}-block-${index}: Animation styles applied, has CSS class: ${!!blockCssAnimationClass}`);
                } else if (hasInputAnimations) {
                    if (DEBUG_SCENES) console.log(`[INPUT_ANIMATION_DEBUG] ${scene.id}-block-${index}: No individual animation, using default visibility`);
                    // If scene has input animations but this specific input doesn't, use default visibility
                    blockVisibility = { opacity: 1 };
                }
                
                const mediaElement = block.media && block.media.render(consistentFps, `${scene.id}-block-${index}`, scene.duration ?? 0, scene.objectFit || 'cover', videoConfig);
                if (DEBUG_SCENES) console.log(`[MEDIA RENDER DEBUG] ${scene.id}-block-${index} media element:`, !!mediaElement);
                
                // Build block className for CSS animations
                let blockClassName = '';
                if (blockCssAnimationClass) {
                    blockClassName = `${blockCssAnimationPrefix}animated ${blockCssAnimationClass}`;
                    if (DEBUG_SCENES) console.log(`[INPUT_CSS_ANIMATION] ${scene.id}-block-${index}: Applying CSS classes: ${blockClassName}`);
                }
                
                return (
                    <div
                        key={`${scene.id}-block-${index}`}
                        style={{
                            position: 'absolute',
                            left: `${block.x * 100}%`,
                            top: `${block.y * 100}%`,
                            width: `${block.width * 100}%`,
                            height: `${block.height * 100}%`,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            // Apply per-input animation styles
                            ...blockAnimationStyles,
                            ...blockVisibility,
                            ...(process.env.NODE_ENV === 'development' && { border: '1px solid cyan' })
                        }}
                        className={blockClassName}
                    >
                        {mediaElement}
                    </div>
                );
            })}
        </div>
    );
};
